import {Ref} from 'vue'
import {getValue} from '@/incoming-balance/CsvColumnMapper.service'
import {CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {format} from 'date-fns'
import {defaultVatOption} from '@/vat/types/VatOptions'
import {CsvGridComponentApi} from '@/incoming-balance/types/CsvGridComponentApi.type'
import {CsvDataRow} from '@/incoming-balance/types/CsvDataRow.type'
import {ColumnMapping} from '@/incoming-balance/types/ColumnMapping.type'
import {DataGridColumnDefinition} from '@/incoming-balance/types/DataGridColumnDefinition.type'
import {AccountEntry} from '@/incoming-balance/types/AccountEntry.type'
import {SubAccountEntry} from '@/incoming-balance/types/SubAccountEntry.type'
import {bookkeepingAccounts, updateMultipleAccountActiveStatus} from '@/bookkeeping-account/bookkeepingAccount.service'

/*
TODO:
 - Fix: Persist draft
 - Fix: Handle same name but different orgNo
  - Fix: Remove CsvToEntries logic ?
 */

// -------- UTILS ---------- //
export const normalizeOrgNo = (orgNo: String) => {
    return orgNo.replace(/\s/g, '')
}

const validAccountNoRegex = /^\d{4}$/

export const calculateTotal = (data: Ref<CsvGridComponentApi>, balanceKey: string): number => {
    if (!data?.value?.csvData?.length) return 0

    const calculatedTotal = data.value.csvData.reduce(
        (total: number, row: CsvDataRow) => {
            const value = getValue(balanceKey, row, data.value.columnMapping)

            //only calc valid accountNos if accountNo column is in mapping
            if('accountNo' in data.value.columnMapping) {
                const accountNo = getValue('accountNo', row, data.value.columnMapping) as string
                if (!accountNo.match(validAccountNoRegex)) {
                    return total
                }
            }

            if (typeof value === 'string') {
                const parsed = parseFloat(value.replace(/\s/g, '').replace(/,/g, '.'))
                if (!isNaN(parsed)) {
                    return total + parsed
                }
            }

            return total
        },
        0
    )

    return Number(calculatedTotal.toFixed(2))
}

export const findTotalFromColMatch = (data: Ref<CsvGridComponentApi>, colMatchKey: string, colMatchValue: string, balanceKey: string): number => {
    const row = data.value.csvData.find((row: CsvDataRow) => {
        const colIndex = data.value.columnMapping[colMatchKey]
        return row[colIndex] === colMatchValue
    })

    if (row) {
        const value = getValue(balanceKey, row, data.value.columnMapping)
        if (typeof value === 'string') {
            const parsed = parseFloat(value.replace(/\s/g, '').replace(/,/g, '.'))
            if (!isNaN(parsed)) {
                return Number(parsed.toFixed(2))
            }
        }
    }

    return 0
}

export const getPostStatus = (amount: number, accountNo: string, isSubAccount: boolean) => {
    if (isSubAccount) {
        return (!amount || amount === 0) ? 'IGNORE' : 'POST'
    } else {
        const validAccountNo = accountNo.match(validAccountNoRegex)
        return (!validAccountNo || amount === 0 || [CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO].includes(accountNo)) ? 'IGNORE' : 'POST'
    }
}

const shouldPost = (line: AccountEntry | SubAccountEntry) => {
    return line.transStatus === 'POST'
}

export const deactivateOtherAccounts = async (accountEntries: AccountEntry[]) => {
    const importAccountNos = new Set(accountEntries.map(e => e.accountNo))

    const toDeactivate = bookkeepingAccounts.value
        .filter(acc => !importAccountNos.has(acc.accountNo))
        .map(acc => {
            return acc.accountNo
        })

    if (toDeactivate.length > 0) {
        await updateMultipleAccountActiveStatus(toDeactivate, false)
    }
}

export const createTransactionLines = (
    date: Date,
    description: string,
    accountEntries: Ref<AccountEntry[]>,
    customerEntries: Ref<SubAccountEntry[]>,
    supplierEntries: Ref<SubAccountEntry[]>
) => {
    return [
        ...accountEntries.value
            .filter(shouldPost)
            .map(createTransactionLineFromEntry(date, description)),

        ...customerEntries.value
            .filter(shouldPost)
            .map(createTransactionLineFromSubAccountEntry(date, description))
            .map(it => ({...it, accountNo: CUSTOMER_ACCOUNT_NO})),

        ...supplierEntries.value
            .filter(shouldPost)
            .map(createTransactionLineFromSubAccountEntry(date, description))
            .map(it => ({...it, accountNo: SUPPLIER_ACCOUNT_NO}))
    ]
}

const createTransactionLineFromEntry = (date: Date, description: string) => (entry: AccountEntry) => {
    return {
        date: format(date, 'yyyy-MM-dd'),
        accountNo: entry.accountNo,
        description: description,
        amount: entry.balance,
        vatOptionId: defaultVatOption.id,
        tags: [],
        subAccountLines: []
    }
}

const createTransactionLineFromSubAccountEntry = (date: Date, description: string) => (entry: SubAccountEntry) => {
    return {
        date: format(date, 'yyyy-MM-dd'),
        accountNo: 'placeholder',
        description: description,
        amount: entry.balance,
        vatOptionId: defaultVatOption.id,
        tags: [],
        subAccountUuid: entry.subAccountUuid,
        invoiceNo: entry?.invoiceNo,
        dueDate: entry?.dueDate,
        subAccountLines: []
    }
}

export const buildIncomingBalanceData = (defaultColMap: ColumnMapping, columnConfig: DataGridColumnDefinition): CsvGridComponentApi => {
    return {
        fileName: '',
        csvData: [],
        firstRowIsHeader: true,
        columnConfig,
        columnMapping: {...defaultColMap},
        fileOptionsOpen: true
    }
}



